import { Autocomplete, TextField } from '@mui/material';
import type { ValueEditorProps } from 'react-querybuilder';
import { useUserListQuery } from 'store/slices/user/user';
import { useState, useEffect, useMemo } from 'react';
import { UserListRequest, UserList } from 'types/user/user';

const AutocompleteValueEditor = ({
    value,
    handleOnChange,
}: ValueEditorProps) => {
    const [nickName, setNickName] = useState<UserListRequest>({ nickname: '' });
    const [inputValue, setInputValue] = useState('');
    const { data } = useUserListQuery(nickName);
    const [selectedUsers, setSelectedUsers] = useState<UserList[]>([]);

    useEffect(() => {
        if (Array.isArray(value) && data) {
            const selected = data.filter((user) => value.includes(user.id));
            setSelectedUsers(selected);
        }
    }, [value, data]);

    const filteredOptions = useMemo(() => {
        const allOptions = data ?? [];
        return [
            ...selectedUsers,
            ...allOptions.filter(
                (user) =>
                    !selectedUsers.some(
                        (selected) => selected.id === user.id,
                    ) &&
                    user.name.toLowerCase().includes(inputValue.toLowerCase()),
            ),
        ];
    }, [data, selectedUsers, inputValue]);

    return (
        <Autocomplete
            multiple
            fullWidth
            options={filteredOptions}
            getOptionLabel={(option) => option.name}
            value={selectedUsers}
            onChange={(_, newValues) => {
                setSelectedUsers(newValues);
                handleOnChange(newValues.map((opt) => opt.id));
                setInputValue('');
            }}
            inputValue={inputValue}
            onInputChange={(_, newValue, reason) => {
                if (reason !== 'reset') {
                    setInputValue(newValue);
                    setNickName({ nickname: newValue.trim() });
                }
            }}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={(params) => (
                <TextField {...params} label={'User List'} />
            )}
        />
    );
};

export default AutocompleteValueEditor;
